import React, { useState, useEffect } from 'react';
import '../styles/Admin.css';
import DashBoardNavBar from "./DashboardNavbar";
import { useNavigate } from "react-router-dom";
import firebase from "../firebaseConfig";
import useProfilePicture from "../hooks/useProfilePicture";
import toast, { toastConfig } from 'react-simple-toasts';
import 'react-simple-toasts/dist/theme/dark.css';
import { FiUser, FiExternalLink } from 'react-icons/fi';
import { FaWhatsapp, FaInstagram, FaLinkedin } from 'react-icons/fa';
import flowTrackerService from "../services/flowTrackerService";
import { useAdminView } from '../contexts/AdminViewContext';
toastConfig({ theme: 'dark' });

const Admin = ({ user }) => {
    const [userData, setUserData] = useState(null);
    const [users, setUsers] = useState([]);
    const [userToEdit, setUserToEdit] = useState(null);
    const [newSubscriptionDate, setNewSubscriptionDate] = useState('');
    const [feedbacks, setFeedbacks] = useState([]);
    const [stats, setStats] = useState({
        totalUsers: 0,
        activeSubscriptions: 0,
        expiredSubscriptions: 0,
        totalFeedbacks: 0
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isVerified, setIsVerified] = useState(false);
    const [isMigratingAll, setIsMigratingAll] = useState(false);
    const [migrationProgress, setMigrationProgress] = useState(null);
    const [globalMigrationStats, setGlobalMigrationStats] = useState(null);
    const [selectedUserData, setSelectedUserData] = useState(null);
    const [showUserDataModal, setShowUserDataModal] = useState(false);
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loadingAnalytics, setLoadingAnalytics] = useState(false);
    const { enterAdminView } = useAdminView();
    const navigate = useNavigate();

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
        
        const checkAdminStatus = async () => {
            try {
                const userDoc = await firebase.firestore().collection('users').doc(user.uid).get();
                const userData = userDoc.data();
                
                if (!userData?.isAdmin) {
                    navigate('/access-denied');
                    return;
                }
                
                setIsVerified(true);
                fetchAllUsers();
                fetchFeedbacks();
            } catch (error) {
                console.error("Error checking admin status:", error);
                navigate('/access-denied');
            }
        };
        
        checkAdminStatus();
    }, [user, navigate]);

    useEffect(() => {
        const fetchFeedbacks = async () => {
            try {
                const feedbacksSnapshot = await firebase.firestore().collection('feedbacks').get();
                const feedbacksData = feedbacksSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setFeedbacks(feedbacksData);
            } catch (error) {
                console.error('Error fetching feedbacks:', error);
            }
        };
        fetchFeedbacks();
        const checkPlanValidity = async () => {
            if (user) {
                const userId = user.uid;
                const docRef = firebase.firestore().collection('users').doc(userId);
                const docSnapshot = await docRef.get();
                if (docSnapshot.exists) {
                    setUserData(docSnapshot.data());
                }
            } else {
                navigate('/login');
            }
        };
        checkPlanValidity();
    }, [user, navigate]);

    useEffect(() => {
        const fetchAllUsers = async () => {
            try {
                const usersSnapshot = await firebase.firestore().collection('users').get();
                const usersData = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setUsers(usersData);

                const now = new Date();
                const activeUsers = usersData.filter(user => {
                    const endDate = user.endSubscriptionDate?.toDate();
                    return endDate && endDate > now;
                });

                setStats({
                    totalUsers: usersData.length,
                    activeSubscriptions: activeUsers.length,
                    expiredSubscriptions: usersData.length - activeUsers.length,
                    totalFeedbacks: feedbacks.length
                });
            } catch (error) {
                console.error('Error fetching users:', error);
            }
        };
        fetchAllUsers();
    }, [feedbacks]);

    const formatDate = (timestamp) => {
        if (timestamp && timestamp.seconds) {
            const date = new Date(timestamp.seconds * 1000);
            const options = { year: 'numeric', month: 'short', day: 'numeric' };
            return date.toLocaleString('en-US', options);
        }
        return 'Date not available';
    };

    const formatEndDate = (date) => {
        if (!date) {
            return <span className="status-no-subscription">No subscription</span>;
        }
        const endDate = new Date(date.seconds * 1000);
        const now = new Date();
        
        if (endDate < now) {
            return <span className="status-expired">{formatDate(date)}</span>;
        }
        return <span className="status-active">{formatDate(date)}</span>;
    };

    const changeUserSubscription = async () => {
        try {
            if (userToEdit && newSubscriptionDate) {
                await firebase.firestore().collection('users').doc(userToEdit.id).update({
                    endSubscriptionDate: new Date(newSubscriptionDate)
                });

                setUsers(users.map(user => {
                    if (user.id === userToEdit.id) {
                        return { ...user, endSubscriptionDate: new Date(newSubscriptionDate) };
                    }
                    return user;
                }));

                setUserToEdit(null);
                setNewSubscriptionDate('');
                window.location.reload(false);
            }
        } catch (error) {
            console.error('Error updating subscription:', error);
        }
    };

    const deleteFeedback = async (feedbackId) => {
        try {
            await firebase.firestore().collection('feedbacks').doc(feedbackId).delete();
            setFeedbacks(feedbacks.filter(feedback => feedback.id !== feedbackId));
        } catch (error) {
            console.error('Error deleting feedback:', error);
        }
    };

    const EditSubscriptionModal = () => {
        return (
            <div className="modal fade show modal-overlay" style={{ display: "block" }}>
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5>Change user subscription</h5>
                            <button
                                className="btn-close"
                                style={{ cursor: "pointer" }}
                                type="button"
                                aria-label="Close"
                                onClick={() => setUserToEdit(null)}
                            ></button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label htmlFor="newSubscriptionDate">New Subscription End :</label>
                                <input
                                    type="date"
                                    id="newSubscriptionDate"
                                    className="form-control"
                                    value={newSubscriptionDate}
                                    onChange={(e) => setNewSubscriptionDate(e.target.value)}
                                />
                                <div className="d-flex justify-content-end mt-3">
                                    <button
                                        type="button"
                                        className="btn btn-secondary me-2"
                                        onClick={() => setUserToEdit(null)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={changeUserSubscription}
                                    >
                                        Save
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const fetchAllUsers = async () => {
        try {
            const usersSnapshot = await firebase.firestore().collection('users').get();
            const usersData = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setUsers(usersData);

            const now = new Date();
            const activeUsers = usersData.filter(user => {
                const endDate = user.endSubscriptionDate?.toDate();
                return endDate && endDate > now;
            });

            setStats({
                totalUsers: usersData.length,
                activeSubscriptions: activeUsers.length,
                expiredSubscriptions: usersData.length - activeUsers.length,
                totalFeedbacks: feedbacks.length
            });
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const fetchFeedbacks = async () => {
        try {
            const feedbacksSnapshot = await firebase.firestore().collection('feedbacks').get();
            const feedbacksData = feedbacksSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setFeedbacks(feedbacksData);
        } catch (error) {
            console.error('Error fetching feedbacks:', error);
        }
    };

    const handleMigrateAllUsers = async () => {
        const confirmMigrate = window.confirm(
            "Are you sure you want to migrate FlowTracker data for ALL users?\n\n" +
            "This will move legacy FlowTrackerData from user documents to the new optimized collection."
        );

        if (!confirmMigrate) return;

        const confirmDelete = window.confirm(
            "Do you also want to DELETE the old FlowTrackerData from user documents after migration?\n\n" +
            "Click OK to delete old data, or Cancel to keep it."
        );

        setIsMigratingAll(true);
        setMigrationProgress({ current: 0, total: 0, currentUser: '' });
        setGlobalMigrationStats(null);

        try {
            const stats = await flowTrackerService.migrateAllUsersData({
                deleteOldData: confirmDelete,
                onProgress: (progress) => {
                    setMigrationProgress(progress);
                }
            });

            setGlobalMigrationStats(stats);
            toast(`Migration complete: ${stats.totalMigratedEntries} entries migrated for ${stats.usersWithLegacyData} users`);
        } catch (error) {
            console.error("Error migrating all users:", error);
            toast("Error during migration: " + error.message);
        } finally {
            setIsMigratingAll(false);
            setMigrationProgress(null);
        }
    };

    const viewUserData = async (userId) => {
        try {
            const userDoc = await firebase.firestore().collection('users').doc(userId).get();
            const userData = userDoc.data();
            setSelectedUserData(userData);
            setShowUserDataModal(true);
        } catch (error) {
            console.error('Error fetching user data:', error);
            toast('Error loading user data');
        }
    };

    const viewAsUser = async (userId) => {
        await enterAdminView(userId, user.uid);
        navigate('/dashboard');
    };

    const calculateAnalytics = async () => {
        setLoadingAnalytics(true);
        try {
            const analytics = {
                totalUsers: users.length,
                usersWithGoals: 0,
                usersWithFlowTracker: 0,
                usersWithLogistics: 0,
                usersWithFlowRoutines: 0,
                usersWithReleaseRecovery: 0,
                usersWithTimeManagement: 0,
                averageFlowQuality: 0,
                totalFlowTrackerEntries: 0,
                usersSharingData: 0,
                usersNotSharingData: 0,
                experiencesByCategory: {},
                subscriptionDistribution: {
                    active: 0,
                    expired: 0,
                    none: 0
                }
            };

            let totalFlowQuality = 0;
            let flowQualityCount = 0;

            for (const user of users) {
                // Check data sharing consent
                if (user.dataSharingConsent) {
                    analytics.usersSharingData++;
                } else {
                    analytics.usersNotSharingData++;
                }

                // Check which tabs have data
                if (user.Goals_1 && Object.keys(user.Goals_1).length > 0) analytics.usersWithGoals++;
                if (user.FlowTrackerData && user.FlowTrackerData.length > 0) analytics.usersWithFlowTracker++;
                if (user.Logistics_8 && user.Logistics_8.length > 0) analytics.usersWithLogistics++;
                if (user.FlowRoutines_4 && user.FlowRoutines_4.length > 0) analytics.usersWithFlowRoutines++;
                if (user.Release_5 && Object.keys(user.Release_5).length > 0) analytics.usersWithReleaseRecovery++;
                if (user.TimeManagement_9 && user.TimeManagement_9.length > 0) analytics.usersWithTimeManagement++;

                // Check FlowTracker data for quality metrics
                if (user.FlowTrackerData && user.FlowTrackerData.length > 0) {
                    analytics.totalFlowTrackerEntries += user.FlowTrackerData.length;
                    user.FlowTrackerData.forEach(entry => {
                        if (entry.meanFlowQuality) {
                            totalFlowQuality += entry.meanFlowQuality;
                            flowQualityCount++;
                        }
                    });
                }

                // Experience categories
                const experience = user.Goals_1?.experience_1;
                if (experience) {
                    analytics.experiencesByCategory[experience] = (analytics.experiencesByCategory[experience] || 0) + 1;
                }

                // Subscription distribution
                if (user.endSubscriptionDate) {
                    const endDate = user.endSubscriptionDate?.toDate();
                    const now = new Date();
                    if (endDate && endDate > now) {
                        analytics.subscriptionDistribution.active++;
                    } else {
                        analytics.subscriptionDistribution.expired++;
                    }
                } else {
                    analytics.subscriptionDistribution.none++;
                }
            }

            analytics.averageFlowQuality = flowQualityCount > 0 ? (totalFlowQuality / flowQualityCount).toFixed(2) : 0;

            setAnalyticsData(analytics);
            toast('Analytics calculated successfully');
        } catch (error) {
            console.error('Error calculating analytics:', error);
            toast('Error calculating analytics');
        } finally {
            setLoadingAnalytics(false);
        }
    };

    const renderGoalsData = (goals) => {
        if (!goals) return <p className="text-muted">No data</p>;
        
        const stripHtml = (html) => {
            if (!html) return 'Not specified';
            const tmp = document.createElement('DIV');
            tmp.innerHTML = html;
            return tmp.textContent || tmp.innerText || 'Not specified';
        };

        return (
            <div>
                <p><strong>Experience:</strong> {goals.experience_1 || 'Not specified'}</p>
                <p><strong>Year:</strong> {goals.year || 'Not specified'}</p>
                <p><strong>Purpose/Goal:</strong></p>
                <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(goals.purpose)}</div>
                <p><strong>Initial Mindset:</strong></p>
                <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(goals.initialMindset)}</div>
                <p><strong>Highlights/Wins:</strong></p>
                {goals.highlights && goals.highlights.length > 0 ? (
                    <ul>
                        {goals.highlights.map((highlight, idx) => (
                            <li key={idx}>{highlight}</li>
                        ))}
                    </ul>
                ) : (
                    <p className="text-muted">No highlights</p>
                )}
                <p><strong>Personal Growth/Lessons:</strong></p>
                <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(goals.growth)}</div>
                <p><strong>Useful Principles:</strong></p>
                <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(goals.principles)}</div>
                <p><strong>Regrets/Losses:</strong></p>
                <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(goals.regrets)}</div>
                {goals.imageUrl && (
                    <p><strong>Image:</strong> <a href={goals.imageUrl} target="_blank" rel="noopener noreferrer">View Image</a></p>
                )}
                <p><strong>Rating:</strong> {goals.rating || 'Not specified'}</p>
            </div>
        );
    };

    const renderFlowTrackerData = (flowTracker) => {
        if (!flowTracker || flowTracker.length === 0) return <p className="text-muted">No data</p>;
        
        const stripHtml = (html) => {
            if (!html) return 'Not specified';
            const tmp = document.createElement('DIV');
            tmp.innerHTML = html;
            return tmp.textContent || tmp.innerText || 'Not specified';
        };

        return (
            <div>
                {flowTracker.map((entry, idx) => (
                    <div key={idx} style={{ borderBottom: '1px solid #eee', padding: '0.5rem 0' }}>
                        <p><strong>Date:</strong> {entry.date || 'Not specified'}</p>
                        <p><strong>Mean Flow Quality:</strong> {entry.meanFlowQuality || 'N/A'}</p>
                        <p><strong>Task:</strong></p>
                        <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(entry.task)}</div>
                        <p><strong>Notes:</strong></p>
                        <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(entry.notes)}</div>
                    </div>
                ))}
            </div>
        );
    };

    const renderLogisticsData = (logistics) => {
        if (!logistics || logistics.length === 0) return <p className="text-muted">No data</p>;
        
        const stripHtml = (html) => {
            if (!html) return 'Not specified';
            const tmp = document.createElement('DIV');
            tmp.innerHTML = html;
            return tmp.textContent || tmp.innerText || 'Not specified';
        };

        return (
            <div>
                {logistics.map((item, idx) => (
                    <div key={idx} style={{ borderBottom: '1px solid #eee', padding: '0.5rem 0' }}>
                        <p><strong>Location:</strong> {item.location || 'Not specified'}</p>
                        <p><strong>City:</strong> {item.city || 'Not specified'}</p>
                        <p><strong>Housing:</strong></p>
                        <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(item.housing)}</div>
                        <p><strong>Working Locations:</strong></p>
                        <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(item.workingLocations)}</div>
                        <p><strong>Cafes:</strong></p>
                        <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(item.cafes)}</div>
                        <p><strong>Snacks:</strong></p>
                        <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(item.snacks)}</div>
                        <p><strong>Training Spots:</strong></p>
                        <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(item.trainingSpots)}</div>
                        <p><strong>Shopping:</strong></p>
                        <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(item.shopping)}</div>
                        <p><strong>Hang Outs:</strong></p>
                        <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(item.hangOuts)}</div>
                        <p><strong>Recovery Cues:</strong></p>
                        <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(item.recoveryCues)}</div>
                        <p><strong>Commuting/Traveling:</strong></p>
                        <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(item.commutingTraveling)}</div>
                    </div>
                ))}
            </div>
        );
    };

    const renderFlowRoutinesData = (flowRoutines) => {
        if (!flowRoutines || flowRoutines.length === 0) return <p className="text-muted">No data</p>;
        
        const stripHtml = (html) => {
            if (!html) return 'Not specified';
            const tmp = document.createElement('DIV');
            tmp.innerHTML = html;
            return tmp.textContent || tmp.innerText || 'Not specified';
        };

        return (
            <div>
                {flowRoutines.map((item, idx) => (
                    <div key={idx} style={{ borderBottom: '1px solid #eee', padding: '0.5rem 0' }}>
                        <p><strong>Type:</strong> {item.type || 'Not specified'}</p>
                        <p><strong>Single Task/Activity:</strong></p>
                        <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(item.singleTask)}</div>
                        <p><strong>Struggle/Challenge:</strong></p>
                        <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(item.struggle)}</div>
                        <p><strong>Challenge Level:</strong></p>
                        <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(item.challengeLevel)}</div>
                        <p><strong>Skill Level:</strong></p>
                        <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(item.skillLevel)}</div>
                        <p><strong>Pre-flow Primer:</strong></p>
                        <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(item.preFlowPrimer)}</div>
                        <p><strong>Impactful Triggers:</strong></p>
                        <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(item.impactfulTriggers)}</div>
                        <p><strong>Amplifiers:</strong></p>
                        <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(item.amplifiers)}</div>
                        <p><strong>Short Release:</strong></p>
                        <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(item.shortRelease)}</div>
                        <p><strong>Short Re-entry Cue:</strong></p>
                        <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(item.shortReentryCue)}</div>
                        <p><strong>Long Release:</strong></p>
                        <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(item.longRelease)}</div>
                        <p><strong>Long Re-entry Cue:</strong></p>
                        <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(item.longReentryCue)}</div>
                        <p><strong>Recovery:</strong></p>
                        <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(item.recovery)}</div>
                    </div>
                ))}
            </div>
        );
    };

    const renderReleaseRecoveryData = (release) => {
        if (!release) return <p className="text-muted">No data</p>;
        
        const stripHtml = (html) => {
            if (!html) return 'Not specified';
            const tmp = document.createElement('DIV');
            tmp.innerHTML = html;
            return tmp.textContent || tmp.innerText || 'Not specified';
        };

        return (
            <div>
                <p><strong>Short Release:</strong></p>
                <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(release.shortRelease)}</div>
                <p><strong>Long Release:</strong></p>
                <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(release.longRelease)}</div>
                <p><strong>Recovery Activities:</strong></p>
                <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>{stripHtml(release.recoveryActivities)}</div>
            </div>
        );
    };

    const renderTimeManagementData = (timeManagement) => {
        if (!timeManagement || timeManagement.length === 0) return <p className="text-muted">No data</p>;
        
        return (
            <div>
                {timeManagement.map((item, idx) => (
                    <div key={idx} style={{ borderBottom: '1px solid #eee', padding: '0.5rem 0' }}>
                        <p><strong>Goal:</strong> {item.goal || 'Not specified'}</p>
                        <p><strong>Category:</strong> {item.category || 'Not specified'}</p>
                        <p><strong>Start:</strong> {item.start || 'Not specified'}</p>
                        <p><strong>End:</strong> {item.end || 'Not specified'}</p>
                    </div>
                ))}
            </div>
        );
    };

    return isVerified ? (
        <div className="admin-container">
            <DashBoardNavBar userName={userData?.username} />
            
            <div className="admin-header">
                <h1>Admin Dashboard</h1>
                <p>Manage users and view platform statistics</p>
            </div>

            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-number">{stats.totalUsers}</div>
                    <div className="stat-label">Total Users</div>
                </div>
                <div className="stat-card">
                    <div className="stat-number">{stats.activeSubscriptions}</div>
                    <div className="stat-label">Active Subscriptions</div>
                </div>
                <div className="stat-card">
                    <div className="stat-number">{stats.expiredSubscriptions}</div>
                    <div className="stat-label">Expired Subscriptions</div>
                </div>
                <div className="stat-card">
                    <div className="stat-number">{stats.totalFeedbacks}</div>
                    <div className="stat-label">Total Feedbacks</div>
                </div>
            </div>

            <div className="admin-section">
                <h2>Data Migration</h2>
                <p>Migrate legacy FlowTrackerData from all user documents to the new optimized collection structure.</p>
                
                <button
                    className="btn btn-warning"
                    onClick={handleMigrateAllUsers}
                    disabled={isMigratingAll}
                    style={{ marginBottom: '1rem' }}
                >
                    {isMigratingAll ? 'Migrating...' : 'Migrate All Users FlowTracker Data'}
                </button>

                {migrationProgress && (
                    <div className="migration-progress" style={{ marginBottom: '1rem' }}>
                        <p>
                            <strong>Progress:</strong> {migrationProgress.current} / {migrationProgress.total} users
                        </p>
                        <p>
                            <strong>Current:</strong> {migrationProgress.currentUser}
                        </p>
                        <div className="progress">
                            <div 
                                className="progress-bar" 
                                role="progressbar" 
                                style={{ width: `${(migrationProgress.current / migrationProgress.total) * 100}%` }}
                            ></div>
                        </div>
                    </div>
                )}

                {globalMigrationStats && (
                    <div className="migration-stats" style={{ backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '8px' }}>
                        <h4>Migration Results</h4>
                        <p><strong>Total Users:</strong> {globalMigrationStats.totalUsers}</p>
                        <p><strong>Users with Legacy Data:</strong> {globalMigrationStats.usersWithLegacyData}</p>
                        <p><strong>Total Legacy Entries:</strong> {globalMigrationStats.totalLegacyEntries}</p>
                        <p><strong>Migrated Entries:</strong> {globalMigrationStats.totalMigratedEntries}</p>
                        <p><strong>Skipped Entries:</strong> {globalMigrationStats.totalSkippedEntries}</p>
                        {globalMigrationStats.errors.length > 0 && (
                            <div style={{ color: 'red' }}>
                                <strong>Errors ({globalMigrationStats.errors.length}):</strong>
                                <ul>
                                    {globalMigrationStats.errors.map((err, idx) => (
                                        <li key={idx}>{err.userId}: {err.error}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="admin-section">
                <h2>Analytics & Statistics</h2>
                <p>Calculate and view platform-wide analytics for scientific/statistical analysis.</p>
                
                <button
                    className="btn btn-primary"
                    onClick={calculateAnalytics}
                    disabled={loadingAnalytics}
                    style={{ marginBottom: '1rem' }}
                >
                    {loadingAnalytics ? 'Calculating...' : 'Calculate Analytics'}
                </button>

                {analyticsData && (
                    <div className="analytics-container" style={{ backgroundColor: '#f8f9fa', padding: '1.5rem', borderRadius: '8px' }}>
                        <h4>Platform Analytics</h4>
                        
                        <div className="stats-grid" style={{ marginBottom: '2rem' }}>
                            <div className="stat-card">
                                <div className="stat-number">{analyticsData.totalUsers}</div>
                                <div className="stat-label">Total Users</div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-number">{analyticsData.averageFlowQuality}</div>
                                <div className="stat-label">Avg Flow Quality</div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-number">{analyticsData.totalFlowTrackerEntries}</div>
                                <div className="stat-label">Total Flow Entries</div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-6">
                                <h5>Data Usage by Feature</h5>
                                <ul>
                                    <li><strong>Goals:</strong> {analyticsData.usersWithGoals} users</li>
                                    <li><strong>Flow Tracker:</strong> {analyticsData.usersWithFlowTracker} users</li>
                                    <li><strong>Logistics:</strong> {analyticsData.usersWithLogistics} users</li>
                                    <li><strong>Flow Routines:</strong> {analyticsData.usersWithFlowRoutines} users</li>
                                    <li><strong>Release & Recovery:</strong> {analyticsData.usersWithReleaseRecovery} users</li>
                                    <li><strong>Time Management:</strong> {analyticsData.usersWithTimeManagement} users</li>
                                </ul>
                            </div>
                            <div className="col-md-6">
                                <h5>Data Sharing Consent</h5>
                                <ul>
                                    <li><strong>Sharing Data:</strong> {analyticsData.usersSharingData} users</li>
                                    <li><strong>Not Sharing:</strong> {analyticsData.usersNotSharingData} users</li>
                                </ul>
                                
                                <h5 className="mt-3">Subscription Distribution</h5>
                                <ul>
                                    <li><strong>Active:</strong> {analyticsData.subscriptionDistribution.active}</li>
                                    <li><strong>Expired:</strong> {analyticsData.subscriptionDistribution.expired}</li>
                                    <li><strong>None:</strong> {analyticsData.subscriptionDistribution.none}</li>
                                </ul>
                            </div>
                        </div>

                        {Object.keys(analyticsData.experiencesByCategory).length > 0 && (
                            <div className="mt-3">
                                <h5>Experiences by Category</h5>
                                <ul>
                                    {Object.entries(analyticsData.experiencesByCategory).map(([category, count]) => (
                                        <li key={category}><strong>{category}:</strong> {count} users</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="admin-section">
                <h2>User Management</h2>
                <div className="table-responsive">
                    <table className="users-table">
                        <thead>
                            <tr>
                                <th>Photo</th>
                                <th>Username</th>
                                <th>Email</th>
                                <th>User ID</th>
                                <th>Lien Social</th>
                                <th>Registration Date</th>
                                <th>Subscription End</th>
                                <th>Data Sharing</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(user => (
                                <tr key={user.id}>
                                    <td className="profile-picture-cell">
                                        {user.profilePicture ? (
                                            <img 
                                                src={user.profilePicture} 
                                                alt={`Photo de ${user.username}`}
                                                className="admin-profile-picture"
                                            />
                                        ) : (
                                            <div className="admin-profile-placeholder">
                                                <FiUser size={20} />
                                            </div>
                                        )}
                                    </td>
                                    <td>{user.username}</td>
                                    <td>{user.email}</td>
                                    <td><small className="text-muted">{user.id}</small></td>
                                    <td className="social-link-cell">
                                        {user.socialLink?.url ? (
                                            <a 
                                                href={user.socialLink.url} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="social-link-admin"
                                                title={`${user.socialLink.type}: ${user.socialLink.url}`}
                                            >
                                                {user.socialLink.type === 'whatsapp' && <FaWhatsapp className="social-icon whatsapp" />}
                                                {user.socialLink.type === 'instagram' && <FaInstagram className="social-icon instagram" />}
                                                {user.socialLink.type === 'linkedin' && <FaLinkedin className="social-icon linkedin" />}
                                                <FiExternalLink size={12} className="external-link-icon" />
                                            </a>
                                        ) : (
                                            <span className="no-social-link">-</span>
                                        )}
                                    </td>
                                    <td>{formatDate(user.accountCreationDate)}</td>
                                    <td>{formatEndDate(user.endSubscriptionDate)}</td>
                                    <td>
                                        {user.dataSharingConsent ? (
                                            <span className="status-active">Yes</span>
                                        ) : (
                                            <span className="status-no-subscription">No</span>
                                        )}
                                    </td>
                                    <td>
                                        <button 
                                            className="edit-button"
                                            onClick={() => {
                                                setUserToEdit(user);
                                                setNewSubscriptionDate('');
                                            }}
                                            style={{ marginRight: '0.5rem' }}
                                        >
                                            Edit Subscription
                                        </button>
                                        <button 
                                            className="edit-button"
                                            onClick={() => viewAsUser(user.id)}
                                            style={{ marginRight: '0.5rem', backgroundColor: '#17a2b8', borderColor: '#17a2b8' }}
                                        >
                                            View as User
                                        </button>
                                        <button 
                                            className="edit-button"
                                            onClick={() => viewUserData(user.id)}
                                        >
                                            View Data
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="admin-section">
                <h2>User Feedbacks</h2>
                <div className="feedback-list">
                    {feedbacks.map((feedback) => (
                        <div key={feedback.id} className="card m-2">
                            <div className="card-header d-flex justify-content-between">
                                <h5>{feedback.title}</h5>
                                <button
                                    className="btn btn-danger"
                                    onClick={() => deleteFeedback(feedback.id)}
                                >
                                    Delete Feedback
                                </button>
                            </div>
                            <div className="p-2">
                                <div>
                                    <b>Description:</b> {feedback.description}
                                </div>
                                <b>Username:</b> {feedback.userName} <u>({feedback.userId})</u>
                                <div>
                                    <b>Timestamp:</b> {formatDate(feedback.timestamp)}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {userToEdit && <EditSubscriptionModal />}
            
            {showUserDataModal && selectedUserData && (
                <div className="modal fade show modal-overlay" style={{ display: "block" }}>
                    <div className="modal-dialog modal-xl">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5>User Data: {selectedUserData.username}</h5>
                                <button
                                    className="btn-close"
                                    style={{ cursor: "pointer" }}
                                    type="button"
                                    aria-label="Close"
                                    onClick={() => {
                                        setShowUserDataModal(false);
                                        setSelectedUserData(null);
                                    }}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-md-6">
                                        <h6>Basic Information</h6>
                                        <p><strong>Email:</strong> {selectedUserData.email}</p>
                                        <p><strong>User ID:</strong> {selectedUserData.id || 'N/A'}</p>
                                        <p><strong>Account Creation:</strong> {formatDate(selectedUserData.accountCreationDate)}</p>
                                        <p><strong>Subscription End:</strong> {formatEndDate(selectedUserData.endSubscriptionDate)}</p>
                                        <p><strong>Data Sharing Consent:</strong> {selectedUserData.dataSharingConsent ? 'Yes' : 'No'}</p>
                                    </div>
                                    <div className="col-md-6">
                                        <h6>Profile</h6>
                                        <p><strong>Username:</strong> {selectedUserData.username}</p>
                                        <p><strong>Experience:</strong> {selectedUserData.Goals_1?.experience_1 || 'Not specified'}</p>
                                        <p><strong>Social Link:</strong> {selectedUserData.socialLink?.url || 'None'}</p>
                                    </div>
                                </div>
                                
                                <hr />
                                
                                <h6>Data by Feature</h6>
                                <div className="row">
                                    <div className="col-md-4">
                                        <div className="card p-2 mb-2" style={{ maxHeight: '400px', overflow: 'auto' }}>
                                            <strong>Goals</strong>
                                            {renderGoalsData(selectedUserData.Goals_1)}
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="card p-2 mb-2" style={{ maxHeight: '400px', overflow: 'auto' }}>
                                            <strong>Flow Tracker</strong>
                                            {renderFlowTrackerData(selectedUserData.FlowTrackerData)}
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="card p-2 mb-2" style={{ maxHeight: '400px', overflow: 'auto' }}>
                                            <strong>Logistics</strong>
                                            {renderLogisticsData(selectedUserData.Logistics_8)}
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="row">
                                    <div className="col-md-4">
                                        <div className="card p-2 mb-2" style={{ maxHeight: '400px', overflow: 'auto' }}>
                                            <strong>Flow Routines</strong>
                                            {renderFlowRoutinesData(selectedUserData.FlowRoutines_4)}
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="card p-2 mb-2" style={{ maxHeight: '400px', overflow: 'auto' }}>
                                            <strong>Release & Recovery</strong>
                                            {renderReleaseRecoveryData(selectedUserData.Release_5)}
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="card p-2 mb-2" style={{ maxHeight: '400px', overflow: 'auto' }}>
                                            <strong>Time Management</strong>
                                            {renderTimeManagementData(selectedUserData.TimeManagement_9)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    ) : null;
};

export default Admin;